import { useAtom, useAtomValue } from "jotai"
import { useTranslation } from "react-i18next"
import { Switch } from "./ui/Switch"
import { BlurryRejectThreshold, ClusterThreshold, FaceRejectThreshold } from "@/lib/catalog/types"
import {
	photoClustersThresholdsAtom,
	sortByScoreInClusterAtom,
	isSuggestingKeepersAtom,
	updateKeepersAndRejects,
	suggestedKeeperCountAtom,
	defaultSuggestedKeeperCount,
	updateKeepersAndRejectsDebounced,
	rejectBlurryPhotosAtom,
	rejectPoorExposureAtom,
	rejectPoorFaceQualityAtom,
	blurryPhotoThresholdAtom,
	poorFaceQualityThresholdAtom,
	groupSimilarPhotosAtom,
	autoAdvanceAfterTaggingAtom,
	minAllowedKeeperCountAtom,
	maxAllowedKeeperCountAtom,
	clampKeeperCount,
} from "@/store/importing/culling-state"
import * as ToggleGroup from "@radix-ui/react-toggle-group"
import { Slider, noopSliderTransform } from "./ui/Slider"
import { isManualCullingAtom, showingCullingGroupTooltipAtom } from "@/store/app-state"
import { Button } from "./ui/Button"
import { FloatingTooltip } from "./ui/FloatingTooltip"
import { allPhotosAtom } from "@/store/photo/photos-state"
import { useEffect } from "react"
import { projectAtom } from "@/store/project/projects-state"
import clsx from "clsx"
import { isAIDebugEnabled } from "@/utils/measurement/feature-flag"

// Simple lookup maps for enum value ↔ display string conversion
// Internal identifiers for ToggleGroup value matching — display text uses labels prop
const blurryThresholdMap: Record<BlurryRejectThreshold, string> = {
	[BlurryRejectThreshold.Lenient]: "Lenient", // i18n-ignore
	[BlurryRejectThreshold.Balanced]: "Balanced", // i18n-ignore
	[BlurryRejectThreshold.Strict]: "Strict", // i18n-ignore
}

const blurryThresholdReverseMap: Record<string, BlurryRejectThreshold> = {
	Lenient: BlurryRejectThreshold.Lenient,
	Balanced: BlurryRejectThreshold.Balanced,
	Strict: BlurryRejectThreshold.Strict,
}

const faceThresholdMap: Record<FaceRejectThreshold, string> = {
	[FaceRejectThreshold.Lenient]: "Lenient", // i18n-ignore
	[FaceRejectThreshold.Balanced]: "Balanced", // i18n-ignore
	[FaceRejectThreshold.Strict]: "Strict", // i18n-ignore
}

const faceThresholdReverseMap: Record<string, FaceRejectThreshold> = {
	Lenient: FaceRejectThreshold.Lenient,
	Balanced: FaceRejectThreshold.Balanced,
	Strict: FaceRejectThreshold.Strict,
}

const CullingToggleGroup = (props: {
	values: string[]
	labels?: Record<string, string>
	currentValue: string | undefined
	onValueChanged: (value: string) => void
	title?: string
	description?: string
	disabled?: boolean
}) => {
	const { values, currentValue, onValueChanged, disabled, description } = props
	const toggleGroupItemClasses = () => {
		let className =
			"hover:text-base-400 data-[state=on]:bg-base-500 data-[state=on]:text-base-100 border-l first:border-none border-base-700 flex items-center justify-center bg-base-600 text-base-300 leading-4 w-full py-1"
		if (disabled) {
			className = clsx(className, "cursor-not-allowed opacity-50")
		}
		return className
	}

	return (
		<div className="flex w-full flex-row items-start justify-between">
			{props.title && <div className="mt-1.5 text-xs text-base-400">{props.title}</div>}
			<div className={clsx(description ? "w-3/4" : "w-full", "flex flex-col space-y-2")}>
				<ToggleGroup.Root
					type="single"
					value={currentValue}
					onValueChange={onValueChanged}
					className="flex w-full flex-1 space-x-0 border border-base-600 text-xs"
				>
					{values.map((value) => (
						<ToggleGroup.Item
							disabled={disabled}
							key={value}
							className={toggleGroupItemClasses()}
							value={value}
						>
							{props.labels?.[value] ?? value}
						</ToggleGroup.Item>
					))}
				</ToggleGroup.Root>
				{props.description && (
					<div className="text-xs text-base-400">{props.description}</div>
				)}
			</div>
		</div>
	)
}

export const CullingGroup = (props: { isSetup: boolean }) => {
	const { t } = useTranslation("components")
	const [groupSimilarPhotos, setGroupSimilarPhotos] = useAtom(groupSimilarPhotosAtom)
	const [sortByScore, setSortByScore] = useAtom(sortByScoreInClusterAtom)
	const [similarityThresh, setSimilarityThresh] = useAtom(photoClustersThresholdsAtom)

	const updateSimilarityThreshold = (val: ClusterThreshold) => {
		if (val !== similarityThresh) {
			setSimilarityThresh(val)
		}
	}

	const similarityValues = ["Loose", "Similar", "Exact"]
	const similarityLabels: Record<string, string> = {
		Loose: t("cullingPanels.loose"),
		Similar: t("cullingPanels.similar"),
		Exact: t("cullingPanels.exact"),
	}

	const clusterThresholdMap: Partial<Record<ClusterThreshold, string>> = {
		[ClusterThreshold.Loose]: "Loose", // i18n-ignore
		[ClusterThreshold.Similar]: "Similar", // i18n-ignore
		[ClusterThreshold.Exact]: "Exact", // i18n-ignore
	}

	const description = () => {
		switch (similarityThresh) {
			case ClusterThreshold.Loose:
				return t("cullingPanels.looseDesc")
			case ClusterThreshold.Similar:
				return t("cullingPanels.similarDesc")
			case ClusterThreshold.Exact:
				return t("cullingPanels.exactDesc")
		}
	}

	const currentSimilarityValue = () => {
		if (!groupSimilarPhotos) {
			return "off"
		}
		return clusterThresholdMap[similarityThresh] ?? "Similar"
	}

	return (
		<div className="flex flex-col space-y-1.5 py-2 text-xs">
			<Button
				variant="custom"
				size="custom"
				className="flex flex-row items-center justify-between pb-1 text-xs"
				trackingLabel="show_groups"
				aria-label={t("cullingPanels.ariaToggleGroupSimilarPhotos")}
			>
				<div>{t("cullingPanels.groupSimilarPhotos")}</div>
				<Switch
					trackingLabel={"similarity_groups"}
					onCheckedChange={(checked) => setGroupSimilarPhotos(checked)}
					checked={groupSimilarPhotos}
				/>
			</Button>
			{isAIDebugEnabled() && (
				<div className="flex flex-row items-center justify-between">
					<div>{t("cullingPanels.sortByScore")}</div>
					<Switch
						trackingLabel={"sort_by_score"}
						onCheckedChange={(checked) => setSortByScore(checked)}
						checked={sortByScore}
					/>
				</div>
			)}

			{groupSimilarPhotos && (
				<CullingToggleGroup
					title={props.isSetup ? t("cullingPanels.sensitivity") : undefined}
					values={similarityValues}
					labels={similarityLabels}
					currentValue={currentSimilarityValue()}
					onValueChanged={(value) => {
						switch (value) {
							case "off":
								setGroupSimilarPhotos(false)
								break
							case "Loose":
								updateSimilarityThreshold(ClusterThreshold.Loose)
								setGroupSimilarPhotos(true)
								break
							case "Similar":
								updateSimilarityThreshold(ClusterThreshold.Similar)
								setGroupSimilarPhotos(true)
								break
							case "Exact":
								updateSimilarityThreshold(ClusterThreshold.Exact)
								setGroupSimilarPhotos(true)
						}
					}}
					description={!props.isSetup ? undefined : description()}
				/>
			)}
		</div>
	)
}

export const CullingSuggestion = (props: { isSetup: boolean }) => {
	const { t } = useTranslation("components")
	const allPhotos = useAtomValue(allPhotosAtom)
	const project = useAtomValue(projectAtom)
	const [suggestKeepers, setSuggestKeepers] = useAtom(isSuggestingKeepersAtom)
	const [count, setCount] = useAtom(suggestedKeeperCountAtom)
	const totalPhotoCount = allPhotos.length
	const minAllowedKeeperCount = useAtomValue(minAllowedKeeperCountAtom)
	const maxAllowedKeeperCount = useAtomValue(maxAllowedKeeperCountAtom)

	const clampToAllowedKeeperCount = (value: number) =>
		clampKeeperCount(value, minAllowedKeeperCount, maxAllowedKeeperCount)

	useEffect(() => {
		if (totalPhotoCount > 0 && project && project.suggestedKeeperCount === undefined) {
			const defaultCount = clampToAllowedKeeperCount(defaultSuggestedKeeperCount())
			setCount(defaultCount)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [totalPhotoCount])

	useEffect(() => {
		const clampedCount = clampToAllowedKeeperCount(count)
		if (clampedCount !== count) {
			setCount(clampedCount)
			if (suggestKeepers) {
				void updateKeepersAndRejectsDebounced(clampedCount)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [count, minAllowedKeeperCount, maxAllowedKeeperCount, suggestKeepers])

	const countSlider = (
		<Slider
			className={props.isSetup ? "w-full" : undefined}
			title={t("cullingPanels.sliderCount")}
			transform={noopSliderTransform}
			min={minAllowedKeeperCount}
			max={maxAllowedKeeperCount}
			step={1}
			value={count}
			onUpdate={(value) => setCount(clampToAllowedKeeperCount(value))}
			onUpdateEnded={(value) =>
				void updateKeepersAndRejectsDebounced(clampToAllowedKeeperCount(value))
			}
			titleAlignment="none"
			disabled={!suggestKeepers}
			displayMax={true}
			displayMaxValue={totalPhotoCount}
		/>
	)

	return (
		<div className="flex flex-col space-y-1 py-2 text-xs">
			<Button
				className="flex w-full flex-row items-center justify-between text-xs"
				trackingLabel="suggest_keepers"
				aria-label={t("cullingPanels.ariaToggleAiTopPicksSuggestions")}
				variant="custom"
				size="custom"
			>
				<div>{t("cullingPanels.showTopPicks")}</div>
				<Switch
					trackingLabel={"suggest_keepers"}
					onCheckedChange={(checked) => {
						setSuggestKeepers(checked)
						if (checked) {
							const nextCount = clampToAllowedKeeperCount(
								count === 0 ? defaultSuggestedKeeperCount() : count,
							)
							setCount(nextCount)
							void updateKeepersAndRejects(nextCount)
						} else {
							setCount(0)
							void updateKeepersAndRejects(0)
						}
					}}
					checked={suggestKeepers}
				/>
			</Button>

			{suggestKeepers && props.isSetup && (
				<div className="flex w-full flex-row items-start justify-between">
					<div className="mt-1.5 text-xs text-base-400">{t("cullingPanels.count")}</div>
					<div className="flex w-3/4 flex-col items-start">
						{countSlider}

						<div className="w-full text-xs text-base-400">
							{t("cullingPanels.topPicksDescription")}
						</div>
					</div>
				</div>
			)}
			{suggestKeepers && !props.isSetup && countSlider}
		</div>
	)
}

export const CullingAutoAdvance = () => {
	const { t } = useTranslation("components")
	const [autoAdvanceAfterTagging, setAutoAdvanceAfterTagging] = useAtom(
		autoAdvanceAfterTaggingAtom,
	)

	return (
		<div className="flex flex-col space-y-1 py-2 text-xs">
			<Button
				className="flex w-full flex-row items-center justify-between text-xs"
				trackingLabel="auto_advance_after_tagging"
				aria-label={t("cullingPanels.ariaToggleAutoAdvance")}
				variant="custom"
				size="custom"
			>
				<div>{t("cullingPanels.autoAdvanceToNextPhoto")}</div>
				<Switch
					trackingLabel={"auto_advance_after_tagging"}
					onCheckedChange={(checked) => setAutoAdvanceAfterTagging(checked)}
					checked={autoAdvanceAfterTagging}
				/>
			</Button>
		</div>
	)
}

export const CullingRejects = (props: { isSetup: boolean }) => {
	const { t } = useTranslation("components")
	const [rejectBlurryPhotos, setRejectBlurryPhotos] = useAtom(rejectBlurryPhotosAtom)
	const [rejectPoorFaceQuality, setRejectPoorFaceQuality] = useAtom(rejectPoorFaceQualityAtom)
	const [rejectPoorExposure, setRejectPoorExposure] = useAtom(rejectPoorExposureAtom)
	const [blurryPhotoThreshold, setBlurryPhotoThreshold] = useAtom(blurryPhotoThresholdAtom)
	const [poorFaceQualityThreshold, setPoorFaceQualityThreshold] = useAtom(
		poorFaceQualityThresholdAtom,
	)
	const keepersCount = useAtomValue(suggestedKeeperCountAtom)
	const showingCullingGroupTooltip = useAtomValue(showingCullingGroupTooltipAtom)
	const isManualCulling = useAtomValue(isManualCullingAtom)

	const thresholdLabels: Record<string, string> = {
		Lenient: t("cullingPanels.lenient"),
		Balanced: t("cullingPanels.balanced"),
		Strict: t("cullingPanels.strict"),
	}

	const blurryThresholdToDisplay = (threshold: BlurryRejectThreshold): string => {
		if (!rejectBlurryPhotos) {
			return "Off"
		}
		return blurryThresholdMap[threshold] ?? "Balanced"
	}

	const displayToBlurryThreshold = (value: string): BlurryRejectThreshold =>
		blurryThresholdReverseMap[value] ?? BlurryRejectThreshold.Balanced

	const faceThresholdToDisplay = (threshold: FaceRejectThreshold): string => {
		if (!rejectPoorFaceQuality) {
			return "Off"
		}
		return faceThresholdMap[threshold] ?? "Balanced"
	}

	const displayToFaceThreshold = (value: string): FaceRejectThreshold =>
		faceThresholdReverseMap[value] ?? FaceRejectThreshold.Balanced

	const blurryPhotoDescription = () => {
		switch (blurryPhotoThreshold) {
			case BlurryRejectThreshold.Lenient:
				return t("cullingPanels.blurryLenientDesc")
			case BlurryRejectThreshold.Balanced:
				return t("cullingPanels.blurryBalancedDesc")
			case BlurryRejectThreshold.Strict:
				return t("cullingPanels.blurryStrictDesc")
		}
	}

	const poorFaceDescription = () => {
		switch (poorFaceQualityThreshold) {
			case FaceRejectThreshold.Lenient:
				return t("cullingPanels.faceLenientDesc")
			case FaceRejectThreshold.Balanced:
				return t("cullingPanels.faceBalancedDesc")
			case FaceRejectThreshold.Strict:
				return t("cullingPanels.faceStrictDesc")
		}
	}

	return (
		<FloatingTooltip
			open={showingCullingGroupTooltip}
			side="right"
			sideOffset={8}
			tooltip={{
				title: t("cullingPanels.smartCullingSettings"),
				main: t("cullingPanels.smartCullingSettingsDesc"),
				index: 1,
				totalCount: isManualCulling ? 3 : 4,
				onboardingKey: isManualCulling
					? "manualCullingTooltipsShown"
					: "cullingTooltipsShown",
			}}
		>
			<div className="flex flex-col space-y-4 py-2 text-xs">
				<div className="flex w-full flex-col items-start space-y-2">
					<Button
						className="flex w-full flex-row items-center justify-between text-xs"
						trackingLabel="reject_blurry_photos"
						aria-label={t("cullingPanels.ariaToggleRejectBlurryPhotos")}
						variant="custom"
						size="custom"
					>
						<div>{t("cullingPanels.rejectBlurryPhotos")}</div>
						<Switch
							trackingLabel={"reject_blurry_photos"}
							onCheckedChange={(checked) => {
								setRejectBlurryPhotos(checked)
								void updateKeepersAndRejects(keepersCount)
							}}
							checked={rejectBlurryPhotos}
						/>
					</Button>

					{rejectBlurryPhotos && (
						<CullingToggleGroup
							title={props.isSetup ? t("cullingPanels.sensitivity") : undefined}
							values={["Lenient", "Balanced", "Strict"]}
							labels={thresholdLabels}
							currentValue={blurryThresholdToDisplay(blurryPhotoThreshold)}
							onValueChanged={(value) => {
								setBlurryPhotoThreshold(displayToBlurryThreshold(value))
								void updateKeepersAndRejects(keepersCount)
							}}
							description={props.isSetup ? blurryPhotoDescription() : undefined}
						/>
					)}
				</div>

				<div className="flex w-full flex-col items-start space-y-2">
					<Button
						className="flex w-full flex-row items-center justify-between text-xs"
						trackingLabel="reject_poor_face_quality"
						aria-label={t("cullingPanels.ariaToggleRejectPoorFaceQuality")}
						variant="custom"
						size="custom"
					>
						<div>{t("cullingPanels.rejectPoorFaceQuality")}</div>
						<Switch
							trackingLabel={"reject_poor_face_quality"}
							onCheckedChange={(checked) => {
								setRejectPoorFaceQuality(checked)
								void updateKeepersAndRejects(keepersCount)
							}}
							checked={rejectPoorFaceQuality}
						/>
					</Button>

					{rejectPoorFaceQuality && (
						<CullingToggleGroup
							title={props.isSetup ? t("cullingPanels.sensitivity") : undefined}
							values={["Lenient", "Balanced", "Strict"]}
							labels={thresholdLabels}
							currentValue={faceThresholdToDisplay(poorFaceQualityThreshold)}
							onValueChanged={(value) => {
								setPoorFaceQualityThreshold(displayToFaceThreshold(value))
								void updateKeepersAndRejects(keepersCount)
							}}
							description={props.isSetup ? poorFaceDescription() : undefined}
						/>
					)}
				</div>

				<Button
					className="flex w-full flex-row items-center justify-between text-xs"
					trackingLabel="reject_poor_exposure"
					aria-label={t("cullingPanels.ariaToggleRejectExposureIssues")}
					variant="custom"
					size="custom"
				>
					<div>{t("cullingPanels.rejectExposureIssues")}</div>
					<Switch
						trackingLabel={"reject_poor_exposure"}
						onCheckedChange={(checked) => {
							setRejectPoorExposure(checked)
							void updateKeepersAndRejects(keepersCount)
						}}
						checked={rejectPoorExposure}
					/>
				</Button>
			</div>
		</FloatingTooltip>
	)
}
